/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import queryString from 'querystring';

import { Endpoints, Infer } from 'shared';

import { ApiClient } from '../types/api/ApiClient';
import { EntityId } from '../types/api/EntityId';
import { IUser } from '../types/user/IUser';
import { UpdateUserNotificationSettingsRequestBody } from '../types/user/UpdateUserNotificationSettingsRequestBody';
import { UpdateUserPasswordRequestBody } from '../types/user/UpdateUserPasswordRequestBody';
import { UpdateUserRequestBody } from '../types/user/UpdateUserRequestBody';
import { getApiEndpoint } from '../utils/getApiEndpoint';
import { apiClientFactory } from '../utils/http-client/apiClientFactory';
import { axiosInstanceFactory } from '../utils/http-client/axiosInstanceFactory';
import { axiosJwtTokenInterceptor, axiosUnauthorizedInterceptor } from '../utils/http-client/axiosInterceptors';

class UserApiServiceClass {
  private api: ApiClient;

  constructor() {
    this.api = apiClientFactory(
      axiosInstanceFactory(
        { baseURL: getApiEndpoint() },
        [axiosJwtTokenInterceptor],
        [[undefined, axiosUnauthorizedInterceptor]],
      ),
    );
  }

  getUser = () => {
    return this.api.get<IUser>('/user');
  };

  updateUser = (
    data: UpdateUserRequestBody | UpdateUserPasswordRequestBody | UpdateUserNotificationSettingsRequestBody,
  ) => {
    return this.api.patch<IUser>('/user', data);
  };

  deleteUser = () => {
    return this.api.del('/user');
  };

  fetchDashboardForUser = () => {
    return this.api.get<Infer<typeof Endpoints.UserDashboardGet>['Response'][200]>(Endpoints.UserDashboardGet.route());
  };

  putUserThumbnail = ({ body }: { body: { type: 'seed'; seed: string } | { type: 'base64'; base64: string } }) => {
    return this.api.put('/user/thumbnail', body);
  };

  deleteUserThumbnail = () => {
    return this.api.del('/user/thumbnail');
  };

  resolveOrganizationInvitation = ({ accept, invitationId }: { invitationId: EntityId; accept: boolean }) => {
    return this.api.post(`/user/invitation/${invitationId}`, { accept });
  };

  getUserThumbnailUrl = ({ v = 1 }: { v?: string | number }) => {
    return `${getApiEndpoint()}/user/thumbnail?${queryString.encode({
      v,
    })}`;
  };

  getUserByIdThumbnailUrl = ({ userId, v = 1 }: { userId: string; v?: string | number }) => {
    return `${getApiEndpoint()}/user/${userId}/thumbnail?${queryString.encode({
      v,
    })}`;
  };

  fetchUserServices = () => {
    return this.api.get<Infer<typeof Endpoints.GetUserServices>['Response'][200]>(Endpoints.GetUserServices.route());
  };

  getFavoriteServices = async () => {
    return this.api.get<Infer<typeof Endpoints.UserFavoriteServiceGet>['Response'][200]>(
      Endpoints.UserFavoriteServiceGet.route(),
    );
  };

  addFavoriteService = (serviceId: string) => {
    return this.api.put<Infer<typeof Endpoints.UserFavoriteServicePut>['Response'][200]>(
      Endpoints.UserFavoriteServicePut.route({ serviceId }),
      {},
    );
  };

  removeFavoriteService = (serviceId: string) => {
    return this.api.del<Infer<typeof Endpoints.UserFavoriteServiceDelete>>(
      Endpoints.UserFavoriteServiceDelete.route({ serviceId }),
    );
  };
}

export const UserApiService = new UserApiServiceClass();
